import { useContext, useEffect, useRef } from "react";
import { InfoTip } from "@nexus/ui";
import {
  defineLabels,
  firstError,
  WidgetIssueState,
  MetricStat,
  resolveLabels,
  unwrap,
  Widget,
  WidgetContentReadyContext,
  WidgetSkeleton,
  WidgetState,
  UpdatingPill,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { CDNSA_CDN_COMPARISON_CONTRACT, type CdnsaCdnComparisonData } from "./types";

export interface CdnsaCdnComparisonConfig {
  title?: string;
  showLegend?: boolean;
  labels?: Record<string, string>;
  /** Optional summary band above the providers; hidden by default. */
  summary?: "shown" | "hidden";
}
const SERIES_LABELS = defineLabels([
  { key: "theft", name: "Theft series", default: "Theft" },
  { key: "leeching", name: "Leeching series", default: "Leeching" },
]);

export const manifest: WidgetManifest = {
  id: "cdnsa-cdn-comparison",
  name: "CDN associations",
  version: "1.0.0",
  description: "Nonexclusive observed associations by CDN and detection type.",
  labels: SERIES_LABELS,
  dataRequests: {
    series: {
      source: "cdnsa-cdn-comparison",
      contract: CDNSA_CDN_COMPARISON_CONTRACT,
      configurable: true,
      sources: ["cdnsa-cdn-comparison"],
    },
  },
};
export const defaultConfig: CdnsaCdnComparisonConfig = {
  title: "CDN associations",
  showLegend: true,
  summary: "hidden",
};

const TYPES = ["theft_for_restreaming", "cdn_leeching"] as const;
const LABELS = ["Theft for restreaming", "CDN leeching"] as const;
const SHORT_LABELS = ["theft", "leeching"] as const;
const COLORS = ["var(--cdnsa-theft-for-restreaming)", "var(--cdnsa-cdn-leeching)"] as const;

const number = new Intl.NumberFormat("en");
const percentage = new Intl.NumberFormat("en", { style: "percent", maximumFractionDigits: 0 });

const UNATTRIBUTED = "__unattributed__";
const OTHER = "__other__";
const providerLabel = (cdn: string) =>
  cdn === UNATTRIBUTED ? "CDN not specified" : cdn === OTHER ? "Other CDNs" : cdn;

export interface ProviderRow {
  cdn: string;
  label: string;
  /** Named providers first; the unattributed and other buckets close the list. */
  bucket: boolean;
  values: [number, number];
  total: number;
}

/**
 * Providers as the widget lists them: named CDNs by their total associations,
 * largest first, then the unattributed and other buckets, always last whatever
 * their size. Associations are nonexclusive (one infringement may involve
 * several CDNs), so totals are compared between rows, never added up.
 */
export function providerRows(points: CdnsaCdnComparisonData["points"]): ProviderRow[] {
  const values = new Map<string, [number, number]>();
  for (const point of points) {
    const row = values.get(point.cdn) ?? [0, 0];
    const index = TYPES.indexOf(point.infringement_type);
    if (index >= 0) row[index] += point.value;
    values.set(point.cdn, row);
  }
  const bucketOrder = (cdn: string) => (cdn === UNATTRIBUTED ? 1 : cdn === OTHER ? 2 : 0);
  return [...values.entries()]
    .map(([cdn, pair]) => ({
      cdn,
      label: providerLabel(cdn),
      bucket: cdn === UNATTRIBUTED || cdn === OTHER,
      values: pair,
      total: pair[0] + pair[1],
    }))
    .sort(
      (left, right) =>
        bucketOrder(left.cdn) - bucketOrder(right.cdn) ||
        right.total - left.total ||
        left.label.localeCompare(right.label),
    );
}

function Legend({ labels }: { labels: readonly string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-metric-detail text-secondary">
      {labels.map((label, index) => (
        <span key={TYPES[index]} className="inline-flex min-w-0 items-center gap-1.5 wrap-anywhere">
          <span
            aria-hidden
            className="h-2 w-3 shrink-0 rounded-[2px]"
            style={{ backgroundColor: COLORS[index] }}
          />
          {label}
        </span>
      ))}
    </div>
  );
}

function Summary({ rows, measureLabel }: { rows: ProviderRow[]; measureLabel: string }) {
  const named = rows.filter((row) => !row.bucket);
  const top = named[0];
  const unattributed = rows.find((row) => row.cdn === UNATTRIBUTED);
  return (
    <dl className="grid shrink-0 grid-cols-2 gap-x-6 gap-y-3 @md/widget:grid-cols-3">
      <MetricStat
        label="Most associated CDN"
        value={top ? top.label : "—"}
        detail={top ? `${number.format(top.total)} ${measureLabel.toLowerCase()}` : undefined}
      />
      <MetricStat label="CDNs observed" value={number.format(named.length)} />
      <MetricStat
        label="CDN not specified"
        value={unattributed ? number.format(unattributed.total) : "0"}
        detail="no CDN identified"
      />
    </dl>
  );
}

/** One provider: its name and total, then its bar, as long as its share of the largest total. */
function ProviderBar({
  row,
  max,
  measureLabel,
  labels,
  shortLabels,
}: {
  row: ProviderRow;
  max: number;
  measureLabel: string;
  labels: readonly string[];
  shortLabels: readonly string[];
}) {
  const shares = row.values.map((value) => (row.total > 0 ? value / row.total : 0));
  const dominant = shares[0] >= shares[1] ? 0 : 1;
  const details = row.values
    .map(
      (value, index) =>
        `${labels[index]}: ${number.format(value)} (${percentage.format(shares[index])})`,
    )
    .join("; ");
  return (
    <li className="min-w-0">
      <div className="mb-2 flex min-w-0 items-baseline justify-between gap-x-4 wall-short:mb-1">
        <span
          className={`min-w-0 wrap-anywhere text-metric-label font-semibold ${row.bucket ? "text-secondary" : "text-primary"}`}
          title={row.label}
        >
          {row.label}
        </span>
        <span className="shrink-0 text-metric-label font-semibold tabular-nums" aria-hidden="true">
          {number.format(row.total)}
          {row.total > 0 ? (
            <span className="ml-2.5 text-metric-detail font-normal text-muted">
              {percentage.format(shares[dominant])} {shortLabels[dominant]}
            </span>
          ) : null}
        </span>
      </div>
      <div
        role="img"
        aria-label={`${row.label}: ${number.format(row.total)} ${measureLabel.toLowerCase()}; ${details}`}
        title={details}
        className="h-2 w-full overflow-hidden rounded-full bg-border/45"
      >
        <div
          className="flex h-full gap-[2px] transition-[width] duration-500 ease-out motion-reduce:transition-none"
          style={{ width: `${max > 0 ? (row.total / max) * 100 : 0}%` }}
        >
          {row.values.map((value, index) =>
            value > 0 ? (
              <span
                key={TYPES[index]}
                className="h-full first:rounded-l-full last:rounded-r-full"
                style={{
                  flexGrow: value,
                  flexBasis: 0,
                  backgroundColor: COLORS[index],
                  opacity: row.bucket ? 0.6 : 1,
                }}
              />
            ) : null,
          )}
        </div>
      </div>
    </li>
  );
}

function CdnsaCdnComparison({ data, config }: WidgetProps<CdnsaCdnComparisonConfig>) {
  const labels = resolveLabels(SERIES_LABELS, config);
  const fullLabels = [
    config.labels?.theft?.trim() || LABELS[0],
    config.labels?.leeching?.trim() || LABELS[1],
  ];
  const shortLabels = [
    config.labels?.theft?.trim() || SHORT_LABELS[0],
    config.labels?.leeching?.trim() || SHORT_LABELS[1],
  ];
  const rendered = useRef(false);
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const series = unwrap<CdnsaCdnComparisonData>(data.series);
  const error = firstError(data, ["series"]);
  const loading = data.series?.isLoading ?? false;
  const empty = !loading && !error && !series?.points.length;
  const rows = series ? providerRows(series.points) : [];
  const max = Math.max(
    0,
    ...rows.filter((row) => row.cdn !== UNATTRIBUTED).map((row) => row.total),
  );
  const showSummary = (config.summary ?? defaultConfig.summary) !== "hidden";
  const measureLabel =
    series?.measure === "accounts"
      ? "Unique accounts"
      : series?.measure === "ips"
        ? "Unique IPs"
        : "Distinct detections";
  const note = "The same pool, account, or IP can appear under multiple CDNs, so the counts overlap.";
  const measureTitle = series?.measure === "accounts"
    ? "Accounts by CDN"
    : series?.measure === "ips"
      ? "IPs by CDN"
      : "Detections by CDN";

  useEffect(() => {
    if (loading || error || empty || !series) return;
    rendered.current = true;
    markContentReady();
  }, [series, loading, error, empty, markContentReady]);

  const ready = !error && !empty && rows.length > 0;
  return (
    <Widget
      title={config.title?.trim() && config.title !== defaultConfig.title ? config.title : measureTitle}
      actions={loading && rendered.current ? <UpdatingPill /> : undefined}
      // Without data the card keeps the height of a few provider rows, level with its row.
      stateMinHeight="var(--widget-content-state-min-height)"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto px-(--density-widget-body-padding) pt-2 pb-3 in-data-fit-screen:gap-3 wall-short:gap-2 wall-short:pt-1 wall-short:pb-2">
          {ready ? (
            <>
              {showSummary ? <Summary rows={rows} measureLabel={measureLabel} /> : null}
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5">
                <div className="flex min-w-0 items-center gap-1.5">
                  <p className="text-metric-caption font-medium text-secondary">{measureLabel}</p>
                  <InfoTip label="CDN associations">{note}</InfoTip>
                </div>
                {config.showLegend !== false ? (
                  <Legend labels={[labels.theft, labels.leeching]} />
                ) : null}
              </div>
              {/* Rows keep a steady rhythm and spread out when the card is taller than its list.
                  Fitted to a screen, the row gets its share of the height: the list asks only
                  for compact spacing and spreads over whatever it is given. */}
              <ul className="flex flex-1 flex-col justify-evenly gap-5 in-data-fit-screen:gap-2.5 wall-short:gap-2.5">
                {rows.flatMap((row, index) => [
                  // The unattributed and other buckets sit apart from the named providers.
                  row.bucket && index > 0 && !rows[index - 1].bucket ? (
                    <li
                      key="buckets"
                      aria-hidden
                      className="-my-1 border-t border-dashed border-border"
                    />
                  ) : null,
                  row.cdn === UNATTRIBUTED ? (
                    <li key={row.cdn} className="text-metric-detail text-muted">
                      {row.label}: <span className="tabular-nums">{number.format(row.total)}</span>
                    </li>
                  ) : (
                    <ProviderBar
                      key={row.cdn}
                      row={row}
                      max={max}
                      measureLabel={measureLabel}
                      labels={fullLabels}
                      shortLabels={shortLabels}
                    />
                  ),
                ])}
              </ul>
            </>
          ) : null}
          {error && <WidgetIssueState error={error} />}
          {empty && (
            <WidgetState
              title="No observed CDN associations"
              description="No observations match the selected period and filters."
            />
          )}
          {loading && !rendered.current && <WidgetSkeleton shape="chart" />}
        </div>
      </div>
    </Widget>
  );
}

export const Component = CdnsaCdnComparison;
const widgetModule: WidgetModule<CdnsaCdnComparisonConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements: () => ({ series: { mode: "select", fields: ["points", "freshness"] } }),
};
export default widgetModule;
